import {
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  Select,
  Radio,
  Table,
  message,
  DatePicker,
  Button,
  Tooltip,
} from 'antd'
import { Link } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { DownloadOutlined } from '@ant-design/icons'
import html2canvas from 'html2canvas'
import dayjs from 'dayjs'
import { extractTextContent } from 'src/utils/stringUtil'
import {
  getPostOverview,
  getChatOverview,
  top10Follwer,
  top10Engagement,
  getSocialHeatMap,
  getSocialDevice,
  getSocialGender,
  top10Impression,
} from 'src/api/reports'
import { getPost } from 'src/api/social'
import './socialoverview.css'
import { dayHours } from 'src/constants'
import moment from 'moment'
import { fetchCountryProvider } from 'src/api/authen'
import DailyPostChart from 'src/components/Charts/DailyPostChart'
import DailyChatChart from 'src/components/Charts/DailyChatChart'
import SocialHeatMap from 'src/components/Charts/SocialHeatMap'
import PieChart from 'src/components/Charts/PieChart'

const { Title } = Typography
const { Column } = Table
const { RangePicker } = DatePicker

const SocialOverview = () => {
  const [selectedCountryValue, setSelectedCountryValue] = useState<
    string | undefined
  >(null)

  const [selectedUserType, setSelectedUserType] = useState<string | undefined>(
    'USER'
  )
  const [selecteTopEngagementType, setSelecteTopEngagementType] = useState<
    string | undefined
  >('ALL')

  const [selectedTopImpressionType, setSelectedTopImpressionType] = useState<
    string | undefined
  >('ALL')

  const [selectedHeatMapUserType, setSelectedHeatMapUserType] = useState<
    string | undefined
  >('ALL')

  const [dayHourFlag, setDayHourFlag] = useState<string | undefined>('both')

  const [topFollowers, setTopFollowers] = useState([])
  const [topEngagement, setTopEngagement] = useState([])
  const [topImpression, setTopImpression] = useState([])
  const [topImpressionloading, setTopImpressionloading] = useState(false)

  const [loading, setLoading] = useState(true)
  const [socialChartData, setSocialChartData] = useState([])
  const [socialChatData, setSocialChatData] = useState([])
  const [socialHeatMapData, setSocialHeatMapData] = useState([])
  const [socialDeviceData, setSocialDeviceData] = useState([])
  const [sociaGenderData, setSocialGenderData] = useState([])
  const [topFollowersloading, setTopFollowersLoading] = useState(false)
  const [topEngagementloading, setTopEngagementloading] = useState(false)
  const [startDate, setStartDate] = useState(
    moment().startOf('day').subtract(1, 'week')
  )
  const [endDate, setEndDate] = useState(moment().startOf('day'))
  const [countries, setCountries] = useState([])
  const cardRefs = useRef([])

  const handleDownload = async (index) => {
    if (!cardRefs.current[index]) return

    const canvas = await html2canvas(cardRefs.current[index], {
      scale: 2,
      ignoreElements: (element) => element.classList.contains('no-snapshot'),
    })
    const image = canvas.toDataURL('image/png')

    const link = document.createElement('a')
    link.href = image
    link.download = `card_snapshot_${index + 1}.png`
    link.click()
  }

  const handleHeatMapAxis = (e) => {
    setDayHourFlag(e.target.value)
  }

  const fetchSocialHeatMap = async () => {
    setTopFollowersLoading(true)
    try {
      const [data] = await Promise.all([
        getSocialHeatMap(
          selectedCountryValue,
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD'),
          selectedHeatMapUserType
        ),
      ])
      await setSocialHeatMapData(data)
    } catch (error) {
      message.error('Failed to load chart data.')
    } finally {
      setTopFollowersLoading(false)
    }
  }

  const fetchSocialDevice = async () => {
    try {
      const data = await getSocialDevice(
        selectedCountryValue,
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      )

      const ios = data.find((d) => d.type === 'EXSCAPE_IOS')?.value || 0
      const android = data.find((d) => d.type === 'EXSCAPE_ANDROID')?.value || 0
      const total = ios + android
      if (total > 1) {
        const output = [
          { key: 'IOS', value: parseFloat((ios / total).toFixed(2)) },
          { key: 'Android', value: +parseFloat((android / total).toFixed(2)) },
        ]
        setSocialDeviceData(output)
      } else {
        setSocialDeviceData([])
      }
    } catch (error) {
      message.error('Failed to load chart data.')
    }
  }

  const fetchSocialGender = async () => {
    try {
      const data = await getSocialGender(
        selectedCountryValue,
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      )
      const male = data.find((d) => d.type === 'MALE')?.value || 0
      const female = data.find((d) => d.type === 'FEMALE')?.value || 0
      const total = male + female
      if (total > 1) {
        const output = [
          { key: 'Male', value: parseFloat((male / total).toFixed(2)) },
          { key: 'Female', value: parseFloat((female / total).toFixed(2)) },
        ]
        setSocialGenderData(output)
      } else {
        setSocialGenderData([])
      }
    } catch (error) {
      message.error('Failed to load Social data.')
    }
  }

  const fetchTopFollwers = async () => {
    setTopFollowersLoading(true)
    try {
      const [data] = await Promise.all([
        top10Follwer(
          selectedCountryValue,
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD'),
          selectedUserType
        ),
      ])
      await setTopFollowers(data)
    } catch (error) {
      message.error('Failed to load chart data.')
    } finally {
      setTopFollowersLoading(false)
    }
  }

  const formatValue = (val: number | string) => {
    const num = Number(val)
    if (isNaN(num)) return val

    if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
    }
    return num.toString()
  }

  const fetchTopPost = async () => {
    setTopEngagementloading(true)
    try {
      const [data] = await Promise.all([
        top10Engagement(
          selectedCountryValue,
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD'),
          selecteTopEngagementType
        ),
      ])
      const dataWithTitles = await Promise.all(
        data.map(async (item) => {
          try {
            const data = await getPost(item.postId)
            return {
              ...item,
              postTitle:
                extractTextContent(data.data?.text)?.trim() || 'Untitled',
            }
          } catch (error) {
            console.error(`Error fetching post for ID ${item.postId}`, error)
            return {
              ...item,
              postTitle: 'Post Deleted',
            }
          }
        })
      )
      await setTopEngagement(dataWithTitles)
    } catch (error) {
      message.error('Failed to load chart data.')
    } finally {
      setTopEngagementloading(false)
    }
  }

  const fetchTopImpression = async () => {
    setTopImpressionloading(true)
    try {
      const [data] = await Promise.all([
        top10Impression(
          selectedCountryValue,
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD'),
          selectedTopImpressionType
        ),
      ])
      const dataWithTitles = await Promise.all(
        data.map(async (item) => {
          try {
            const data = await getPost(item.postId)
            return {
              ...item,
              postTitle:
                extractTextContent(data.data?.text)?.trim() || 'Untitled',
            }
          } catch (error) {
            console.error(`Error fetching post for ID ${item.postId}`, error)
            return {
              ...item,
              postTitle: 'Post Deleted',
            }
          }
        })
      )
      await setTopImpression(dataWithTitles)
    } catch (error) {
      message.error('Failed to load chart data.')
    } finally {
      setTopImpressionloading(false)
    }
  }

  const fetchSocialChartData = async () => {
    try {
      const [chartData] = await Promise.all([
        getPostOverview(
          selectedCountryValue,
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD')
        ),
      ])
      await setSocialChartData(chartData)
    } catch (error) {
      message.error('Failed to load chart data.')
    } finally {
      setLoading(false)
    }
  }

  const _countryChange = (value: string) => {
    setSelectedCountryValue(value)
  }

  const _userTypeChange = (value: string) => {
    setSelectedUserType(value)
  }

  const _topEngagemetTypeChange = (value: string) => {
    setSelecteTopEngagementType(value)
  }

  const _topImpressionTypeChange = (value: string) => {
    setSelectedTopImpressionType(value)
  }

  const _engagemetTypeChange = (value: string) => {
    setSelectedHeatMapUserType(value)
  }

  const fetchSocialChatChartData = async () => {
    try {
      const [chartData] = await Promise.all([
        getChatOverview(
          selectedCountryValue,
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD')
        ),
      ])
      await setSocialChatData(chartData)
    } catch (error) {
      message.error('Failed to load chart data.')
    } finally {
      setLoading(false)
    }
  }

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = [
        { label: 'All', value: null },
        ...data.data.map((item) => ({
          label: item.countryName,
          value: item.countryCode,
        })),
      ]
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    fetchCountry()
    fetchSocialChartData()
    fetchSocialChatChartData()
    fetchTopFollwers()
    fetchTopPost()
    fetchTopImpression()
    fetchSocialHeatMap()
    fetchSocialDevice()
    fetchSocialGender()
  }, [])

  useEffect(() => {
    fetchSocialChartData()
    fetchSocialChatChartData()
    fetchTopFollwers()
    fetchTopPost()
    fetchTopImpression()
    fetchSocialHeatMap()
    fetchSocialDevice()
    fetchSocialGender()
  }, [startDate, endDate, selectedCountryValue])

  useEffect(() => {
    fetchTopFollwers()
  }, [selectedUserType])

  useEffect(() => {
    fetchTopPost()
  }, [selecteTopEngagementType])

  useEffect(() => {
    fetchTopImpression()
  }, [selectedTopImpressionType])

  useEffect(() => {
    fetchSocialHeatMap()
  }, [selectedHeatMapUserType])

  useEffect(() => {
    fetchSocialHeatMap()
  }, [dayHourFlag])

  return (
    <div className="dashboard-wrap">
      <div className="top-content">
        <Row gutter={25}>
          <Col flex={200}>
            <Title level={4}>Social Overview</Title>
          </Col>
          <Col flex={6}>
            <Select
              value={selectedCountryValue}
              style={{ width: 200 }}
              onChange={_countryChange}
              options={countries}
              loading={loading}
            >
              {countries.map((country) => (
                <Select.Option value={country.value} key={country.value}>
                  {country.label}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col flex={6}>
            <RangePicker
              style={{ width: 300 }}
              allowClear={false}
              value={[startDate, endDate]}
              disabledDate={(current) =>
                current && current > dayjs().endOf('day')
              }
              onChange={(dates) => {
                if (dates) {
                  setStartDate(dates[0])
                  setEndDate(dates[1])
                } else {
                  setStartDate(null)
                  setEndDate(null)
                }
              }}
            />
          </Col>
        </Row>
      </div>

      <div>
        <Row gutter={25} className="overall-chart">
          <Col span={10}>
            <Card
              ref={(el) => (cardRefs.current[0] = el)}
              title="Total Posts"
              extra={
                <Button
                  className="no-snapshot"
                  type="primary"
                  style={{
                    background: 'transparent',
                    borderColor: 'transparent',
                  }}
                  shape="circle"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(0)}
                />
              }
              style={{ height: '100%' }}
            >
              <Row gutter={25}>
                <Col span={8}>
                  <Card
                    style={{
                      backgroundColor: 'transparent',
                      border: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Posts</span>}
                      value={(socialChartData as any).totalPost}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card
                    style={{
                      backgroundColor: 'transparent',
                      border: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Reach</span>}
                      value={(socialChartData as any).totalReach}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card
                    style={{
                      backgroundColor: 'transparent',
                      border: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Clicks</span>}
                      value={(socialChartData as any).totalClick}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
              </Row>

              <DailyPostChart data={socialChartData} chartHeight={400} />
            </Card>
          </Col>
          <Col span={14}>
            <Card
              ref={(el) => (cardRefs.current[1] = el)}
              title="Total Engagement"
              style={{ height: '100%' }}
              extra={
                <Button
                  className="no-snapshot"
                  type="primary"
                  style={{
                    background: 'transparent',
                    borderColor: 'transparent',
                  }}
                  shape="circle"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(1)}
                />
              }
            >
              <Row gutter={25}>
                <Col span={8}>
                  <Card
                    style={{
                      backgroundColor: 'transparent',
                      border: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Likes</span>}
                      value={(socialHeatMapData as any).totalLikes}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card
                    style={{
                      backgroundColor: 'transparent',
                      border: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Comments</span>}
                      value={(socialHeatMapData as any).totalComments}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card
                    style={{
                      backgroundColor: 'transparent',
                      border: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Shares</span>}
                      value={(socialHeatMapData as any).totalShare}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
              </Row>

              <div className="stats-card-wrap">
                <Select
                  value={selectedHeatMapUserType}
                  style={{ width: 150 }}
                  onChange={_engagemetTypeChange}
                  options={[
                    { label: 'All', value: 'ALL' },
                    { label: 'Likes', value: 'LIKES' },
                    { label: 'Comments', value: 'COMMENTS' },
                    { label: 'Shares', value: 'SHARES' },
                  ]}
                />
                <Radio.Group
                  className="rounded-radio-group"
                  size={'middle'}
                  value={dayHourFlag}
                  onChange={handleHeatMapAxis}
                >
                  {dayHours.map((dateType) => (
                    <Radio.Button key={dateType.value} value={dateType.value}>
                      {dateType.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </div>
              <SocialHeatMap
                key={`${dayHourFlag}-${JSON.stringify(socialHeatMapData)}`}
                data={socialHeatMapData}
                axisVisibility={dayHourFlag}
              />
            </Card>
          </Col>
        </Row>
        <Row gutter={25}>
          <Col span={10} style={{ display: 'flex', height: 420, width: 400 }}>
            <Card
              ref={(el) => (cardRefs.current[2] = el)}
              title="Total Chats"
              style={{ flex: 1 }}
              extra={
                <Button
                  type="primary"
                  className="no-snapshot"
                  style={{
                    background: 'transparent',
                    borderColor: 'transparent',
                  }}
                  shape="circle"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(2)}
                />
              }
            >
              <Row gutter={25}>
                <Col span={12}>
                  <Card
                    style={{
                      background: 'transparent',
                      borderColor: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Chats</span>}
                      value={(socialChatData as any).totalChat}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    style={{
                      background: 'transparent',
                      borderColor: 'transparent',
                    }}
                  >
                    <Statistic
                      title={<span>Total Messages</span>}
                      value={(socialChatData as any).totalMessage}
                      formatter={(val) => (
                        <span
                          style={{
                            color: '#5c6ef2',
                            fontSize: '3rem',
                            fontWeight: 600,
                          }}
                        >
                          {formatValue(val)}
                        </span>
                      )}
                    />
                  </Card>
                </Col>
              </Row>

              <div style={{ display: 'flex', height: 420, width: 400 }}>
                <DailyChatChart
                  data={socialChatData}
                  chartHeight={180}
                  chartWidth={470}
                />
              </div>
            </Card>
          </Col>

          <Col span={7} style={{ height: 420, width: 400, display: 'flex' }}>
            <Card
              ref={(el) => (cardRefs.current[3] = el)}
              title="Devices"
              style={{ flex: 1 }}
              extra={
                <Button
                  type="primary"
                  className="no-snapshot"
                  style={{
                    background: 'transparent',
                    borderColor: 'transparent',
                  }}
                  shape="circle"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(3)}
                />
              }
            >
              <PieChart
                data={socialDeviceData}
                colorKey={'IOS'}
                pieColors={['#648858', '#4754a6']}
              />
            </Card>
          </Col>

          <Col span={7} style={{ display: 'flex', height: 420, width: 400 }}>
            <Card
              ref={(el) => (cardRefs.current[4] = el)}
              title="Gender"
              style={{ flex: 1 }}
              extra={
                <Button
                  type="primary"
                  className="no-snapshot"
                  style={{
                    background: 'transparent',
                    borderColor: 'transparent',
                  }}
                  shape="circle"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(4)}
                />
              }
            >
              <PieChart
                colorKey={'Male'}
                data={sociaGenderData}
                pieColors={['#5362b8', '#ca64a2']}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <div className="bottom-content" style={{ marginTop: 20 }}>
        <Row gutter={25}>
          <Col span={8}>
            <Card className="list-card">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  Top Followers
                </Title>
                <Select
                  value={selectedUserType}
                  onChange={_userTypeChange}
                  options={[
                    { label: 'Bot', value: 'BOT' },
                    { label: 'User', value: 'USER' },
                  ]}
                />
              </div>
              <div>
                <Table
                  dataSource={topFollowers}
                  loading={topFollowersloading}
                  pagination={false}
                  scroll={{ y: 300 }}
                >
                  <Table.Column
                    title="User Name"
                    dataIndex="username"
                    key="url"
                    render={(_, record) => {
                      const r = record as { username: string }
                      return selectedUserType === 'USER' ? (
                        <Link to={`/users/detail/${r.username}`}>
                          <Typography.Text style={{ color: '#0682fa' }}>
                            {r.username}
                          </Typography.Text>
                        </Link>
                      ) : (
                        <Typography.Text>{r.username}</Typography.Text>
                      )
                    }}
                  />
                  <Table.Column
                    title="Total"
                    dataIndex="follower"
                    key="follower"
                  />
                </Table>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card className="list-card">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  Top 10 Engagment
                </Title>
                <Select
                  value={selecteTopEngagementType}
                  style={{ width: 150 }}
                  onChange={_topEngagemetTypeChange}
                  options={[
                    { label: 'All', value: 'ALL' },
                    { label: 'Likes', value: 'LIKES' },
                    { label: 'Comments', value: 'COMMENTS' },
                    { label: 'Shares', value: 'SHARES' },
                  ]}
                />
              </div>
              <div>
                <Table
                  dataSource={topEngagement}
                  loading={topEngagementloading}
                  pagination={false}
                  scroll={{ y: 300 }}
                >
                  <Column
                    title="Post Title"
                    dataIndex="postTitle"
                    key="postTitle"
                    render={(_, record) => {
                      const r = record as { postId: string; postTitle: string }
                      if (r.postTitle === 'Post Deleted') {
                        return <Typography.Text>{r.postTitle}</Typography.Text>
                      }
                      return (
                        <Link to={`/social/user-posts/${r.postId}`}>
                          <Tooltip title={r.postTitle}>
                            <Typography.Text style={{ color: '#0682fa' }}>
                              {r.postTitle?.length > 30
                                ? r.postTitle?.substring(0, 30) + '...'
                                : r.postTitle}
                            </Typography.Text>
                          </Tooltip>
                        </Link>
                      )
                    }}
                  />
                  <Column
                    title="User"
                    dataIndex="userName"
                    key="userName"
                    render={(_, record) => {
                      const r = record as { userName: string }
                      return selectedUserType === 'USER' ? (
                        <Link to={`/users/detail/${r.userName}`}>
                          <Typography.Text style={{ color: '#0682fa' }}>
                            {r.userName}
                          </Typography.Text>
                        </Link>
                      ) : (
                        <Typography.Text>{r.userName}</Typography.Text>
                      )
                    }}
                  />

                  <Column title="Total" dataIndex="count" key="count" />
                </Table>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card className="list-card">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  Top 10 Impressions
                </Title>
                <Select
                  value={selectedTopImpressionType}
                  style={{ width: 150 }}
                  onChange={_topImpressionTypeChange}
                  options={[
                    { label: 'All', value: 'ALL' },
                    { label: 'Views', value: 'VIEWS' },
                    { label: 'Clicks', value: 'CLICKS' },
                    { label: 'Reach', value: 'REACH' },
                  ]}
                />
              </div>
              <div>
                <Table
                  dataSource={topImpression}
                  loading={topImpressionloading}
                  pagination={false}
                  scroll={{ y: 300 }}
                >
                  <Column
                    title="Post Title"
                    dataIndex="postTitle"
                    key="postTitle"
                    render={(_, record) => {
                      const r = record as { postId: string; postTitle: string }
                      if (r.postTitle === 'Post Deleted') {
                        return <Typography.Text>{r.postTitle}</Typography.Text>
                      }
                      return (
                        <Link to={`/social/user-posts/${r.postId}`}>
                          <Tooltip title={r.postTitle}>
                            <Typography.Text style={{ color: '#0682fa' }}>
                              {r.postTitle?.length > 30
                                ? r.postTitle?.substring(0, 30) + '...'
                                : r.postTitle}
                            </Typography.Text>
                          </Tooltip>
                        </Link>
                      )
                    }}
                  />

                  <Column
                    title="User"
                    dataIndex="userName"
                    key="userName"
                    render={(_, record) => {
                      const r = record as { userName: string }
                      return selectedUserType === 'USER' ? (
                        <Link to={`/users/detail/${r.userName}`}>
                          <Typography.Text style={{ color: '#0682fa' }}>
                            {r.userName}
                          </Typography.Text>
                        </Link>
                      ) : (
                        <Typography.Text>{r.userName}</Typography.Text>
                      )
                    }}
                  />
                  <Column title="Total" dataIndex="count" key="count" />
                </Table>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default SocialOverview
